import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import Header from "../components/Header";
import LeagueContextActions from "../components/LeagueContextActions";
import WeekSelector from "../components/WeekSelector";
import PlayoffOddsTable from "../components/PlayoffOddsTable";
import RankDistributionTable from "../components/RankDistributionTable";
import SeedingOutcomesTable from "../components/SeedingOutcomesTable";
import RemainingStrengthOfScheduleTable from "../components/RemainingStrengthOfScheduleTable";
import ProviderBetaBanner from "../components/ProviderBetaBanner";
import { safeFetch } from "../utils/api";

const MIN_WEEK_TO_SIMULATE = 4;

const SIMULATION_OPTIONS = [50, 100, 250];
const SIMULATION_TABS = [
    { value: "odds", label: "Playoff Odds", field: "playoff_odds" },
    { value: "finish-range", label: "Final Standings Odds", field: "rank_distribution" },
    { value: "seeding", label: "Seeding Outcomes", field: "seeding_outcomes" },
    { value: "schedule", label: "Remaining Schedule" },
];

export const isWeekDataPending = (selectedWeek, nCompletedWeeks) => (
    selectedWeek !== null
    && nCompletedWeeks !== null
    && selectedWeek > nCompletedWeeks
);

export const getLatestCompletedSimulationWeek = (nCompletedWeeks, regularSeasonWeeks = null) => {
    if (nCompletedWeeks === null || nCompletedWeeks === "") return null;

    const completedWeek = Number(nCompletedWeeks);
    if (!Number.isInteger(completedWeek)) return null;

    if (regularSeasonWeeks === null || regularSeasonWeeks === "") return completedWeek;

    const regularSeasonWeek = Number(regularSeasonWeeks);
    return Number.isInteger(regularSeasonWeek)
        ? Math.min(completedWeek, regularSeasonWeek)
        : completedWeek;
};

export const normalizeSimulationWeek = (requestedWeek, latestCompletedWeek) => {
    if (!Number.isInteger(latestCompletedWeek) || latestCompletedWeek < MIN_WEEK_TO_SIMULATE) {
        return null;
    }

    const parsedWeek = Number(requestedWeek);
    if (requestedWeek === null || requestedWeek === "" || !Number.isInteger(parsedWeek)) {
        return latestCompletedWeek;
    }

    return Math.min(
        Math.max(parsedWeek, MIN_WEEK_TO_SIMULATE),
        latestCompletedWeek
    );
};

const LeagueSimulationPage = () => {
    const { provider, leagueYear, leagueId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [leagueData, setLeagueData] = useState(null);
    const [simulationData, setSimulationData] = useState(null);
    const [activeTab, setActiveTab] = useState(
        () => new URLSearchParams(location.search).get("tab") || "odds"
    );
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(null);
    const [nCompletedWeeks, setNCompletedWeeks] = useState(null);
    const [leagueSettings, setLeagueSettings] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    const latestCompletedSimulationWeek = getLatestCompletedSimulationWeek(
        nCompletedWeeks,
        leagueSettings?.n_regular_season_weeks
    );

    const queryParams = new URLSearchParams(location.search);
    const [nSimulations, setNSimulations] = useState(
        queryParams.get("n_simulations")
            ? parseInt(queryParams.get("n_simulations"))
            : null
    );

    if (fetchError) {
        throw fetchError;
    }

    // Fetch league metadata
    useEffect(() => {
        safeFetch(`/api/league/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setLeagueData(data);
                }
            })
            .catch((err) => {
                console.error("ERROR: /api/league/:provider/:year/:id", leagueYear, leagueId, err);
                setFetchError(err);
            });
    }, [leagueYear, leagueId, navigate]);

    // Check league status
    useEffect(() => {
        safeFetch(`/api/check-league-status/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) navigate(data.redirect);
            })
            .catch((err) => {
                console.error("ERROR: /api/check-league-status");
                setFetchError(err);
            });
    }, [leagueYear, leagueId, navigate]);

    // Fetch league settings
    useEffect(() => {
        safeFetch(`/api/league-settings/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setLeagueSettings(data);
                }
            })
            .catch((err) => {
                console.error("ERROR: /api/league-settings/:provider/:year/:id", leagueYear, leagueId, err);
                setFetchError(err);
            });
    }, [leagueYear, leagueId, navigate]);

    // Fetch the current and completed week boundaries.
    useEffect(() => {
        safeFetch(`/api/league/${provider}/${leagueYear}/${leagueId}/current-week/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setCurrentWeek(data.current_week);
                    setNCompletedWeeks(data.n_completed_weeks);
                }
            })
            .catch((err) => {
                console.error("ERROR: /api/league/:provider/:year/:id/current-week", leagueYear, leagueId, err);
                setFetchError(err);
            });
    }, [leagueYear, leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Select only completed regular-season weeks. Invalid URL weeks are
    // clamped to the nearest available boundary and canonicalized in place.
    useEffect(() => {
        if (latestCompletedSimulationWeek === null || latestCompletedSimulationWeek < MIN_WEEK_TO_SIMULATE) {
            return;
        }

        const qp = new URLSearchParams(location.search);
        const requestedWeek = qp.get("week");
        const normalizedWeek = normalizeSimulationWeek(
            requestedWeek,
            latestCompletedSimulationWeek
        );

        setSelectedWeek(normalizedWeek);

        if (requestedWeek !== null && Number(requestedWeek) !== normalizedWeek) {
            qp.set("week", normalizedWeek);
            navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
        }
    }, [latestCompletedSimulationWeek, location.pathname, location.search, navigate]);

    // Redirect if too early
    useEffect(() => {
        if (latestCompletedSimulationWeek !== null && latestCompletedSimulationWeek < MIN_WEEK_TO_SIMULATE) {
            navigate(`/fantasy_stats/uh-oh-too-early/league-homepage/${provider}/${leagueYear}/${leagueId}`);
        }
    }, [latestCompletedSimulationWeek, provider, leagueYear, leagueId, navigate]);

    // Fetch only the active simulation table. The backend shares one durable
    // Monte Carlo result across the three simulation endpoints.
    useEffect(() => {
        if (selectedWeek === null || activeTab === "schedule") return;

        const qp = new URLSearchParams();
        if (nSimulations !== null) qp.set("n_simulations", nSimulations);
        if (selectedWeek !== null) qp.set("week", selectedWeek);

        const endpoint = `/api/playoff-simulation/${activeTab}/${provider}/${leagueYear}/${leagueId}/?${qp.toString()}`;

        safeFetch(endpoint, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    const tab = SIMULATION_TABS.find((item) => item.value === activeTab);
                    setSimulationData({ [tab.field]: data.data, cached: data.cached });
                }
            })
            .catch((err) => {
                console.error("ERROR:", endpoint, err);
                setFetchError(err);
            });
    }, [activeTab, selectedWeek, nSimulations, provider, leagueYear, leagueId, navigate]);

    const handleWeekChange = (newWeek) => {
        const validWeek = newWeek && !isNaN(newWeek) ? parseInt(newWeek) : currentWeek;
        setSelectedWeek(validWeek);
        setSimulationData(null);
        const qp = new URLSearchParams(location.search);
        qp.set("week", validWeek);
        window.history.replaceState(null, "", `${location.pathname}?${qp.toString()}`);
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        setSimulationData(null);
        const qp = new URLSearchParams(location.search);
        qp.set("tab", newTab);
        window.history.replaceState(null, "", `${location.pathname}?${qp.toString()}`);
    };

    const handleSimulationsChange = (newN) => {
        const val = newN === "--" ? null : parseInt(newN, 10);
        setNSimulations(val);
        setSimulationData(null);
        const qp = new URLSearchParams(location.search);
        if (val !== null) {
            qp.set("n_simulations", val);
        } else {
            qp.delete("n_simulations");
        }
        window.history.replaceState(null, "", `${location.pathname}?${qp.toString()}`);
    };

    const pageDataReady = Boolean(
        leagueData
        && leagueSettings
        && currentWeek !== null
        && selectedWeek !== null
        && latestCompletedSimulationWeek >= MIN_WEEK_TO_SIMULATE
    );

    return (
        <div className="min-h-screen bg-bg">
            <Header />

            {/* Hero Band */}
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                Playoff Simulation
                            </p>
                            <h1 className="font-display text-4xl font-black text-white leading-none">
                                {leagueData?.league_name || "Loading league…"}
                            </h1>
                            <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                {leagueData
                                    ? `${leagueYear} Season · League ID: ${leagueData.league_id}`
                                    : "Preparing playoff simulation"}
                            </p>
                        </div>
                        <LeagueContextActions
                            league={leagueData}
                            provider={provider}
                            leagueYear={leagueYear}
                            leagueId={leagueId}
                            activePage="simulation"
                        />
                    </div>
                </div>
            </div>

            {/* Controls + Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <ProviderBetaBanner provider={provider} leagueId={leagueId} leagueYear={leagueYear} leagueName={leagueData?.league_name} />

                <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
                <div className="mb-6 rounded-lg border border-border bg-surface p-1 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Tabs.List className="flex flex-wrap gap-1 px-1">
                        {SIMULATION_TABS.map((tab) => <Tabs.Trigger key={tab.value} value={tab.value} className="rounded px-3 py-2 text-xs font-bold uppercase tracking-wide text-text-muted transition-colors hover:text-forest data-[state=active]:bg-forest data-[state=active]:text-white">{tab.label}</Tabs.Trigger>)}
                    </Tabs.List>
                {/* Controls Row: Week Selector + Simulation Selector */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-1 pb-1">
                    {pageDataReady ? (
                        <>
                            <WeekSelector
                                selectedWeek={selectedWeek}
                                minWeek={MIN_WEEK_TO_SIMULATE}
                                maxWeek={latestCompletedSimulationWeek}
                                onWeekChange={handleWeekChange}
                                disable={leagueSettings.regular_season_complete}
                            />

                            <div className="flex items-center gap-2 font-sans text-sm text-text-muted">
                                <span>Simulations:</span>
                                <select
                                    value={nSimulations ?? "--"}
                                    onChange={(e) => handleSimulationsChange(e.target.value)}
                                    className="bg-white border border-border rounded px-3 py-1.5 text-sm font-sans text-ds-text focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green"
                                >
                                    <option value="--">Default</option>
                                    {SIMULATION_OPTIONS.map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-3" role="status" aria-label="Loading simulation controls">
                            <span className="h-9 w-28 animate-pulse rounded-lg bg-forest/10" />
                            <span className="h-9 w-32 animate-pulse rounded-lg bg-forest/10" />
                        </div>
                    )}
                </div>
                    </div>
                </div>

                <Tabs.Content value="odds" className="outline-none">
                    <PlayoffOddsTable
                        data={simulationData?.playoff_odds || null}
                        playoffTeams={leagueSettings?.n_playoff_spots}
                        selectedWeek={selectedWeek}
                        pendingData={isWeekDataPending(selectedWeek, nCompletedWeeks)}
                        cached={simulationData?.cached}
                    />
                </Tabs.Content>

                <Tabs.Content value="finish-range" className="outline-none">
                    <RankDistributionTable
                        data={simulationData?.rank_distribution || null}
                        numColumns={leagueSettings?.n_teams}
                        playoffTeams={leagueSettings?.n_playoff_spots}
                        selectedWeek={selectedWeek}
                        pendingData={isWeekDataPending(selectedWeek, nCompletedWeeks)}
                    />
                </Tabs.Content>

                <Tabs.Content value="seeding" className="outline-none">
                    <SeedingOutcomesTable
                        data={simulationData?.seeding_outcomes || null}
                        playoffTeams={leagueSettings?.n_playoff_spots}
                        selectedWeek={selectedWeek}
                        pendingData={isWeekDataPending(selectedWeek, nCompletedWeeks)}
                    />
                </Tabs.Content>

                <Tabs.Content value="schedule" className="outline-none">
                    <RemainingStrengthOfScheduleTable
                        provider={provider}
                        leagueYear={leagueYear}
                        leagueId={leagueId}
                        week={selectedWeek}
                        nCompletedWeeks={nCompletedWeeks}
                    />
                </Tabs.Content>
                </Tabs.Root>
            </main>
        </div>
    );
};

export default LeagueSimulationPage;
